import React from 'react';

import {Spinner} from '../../Spinner';
import View from '../../View';
import {useSearchableListContext} from '../useSearchableList';
import {VirtualizedSearchableListBox} from './VirtualizedSearchableListBox';
import {NonVirtualizedSearchableListBox} from './NonVirtualizedSearchableListBox';
import type {SearchableListBoxProps} from './_types';

type SearchableListBoxWrapperProps =
  | ({
      isVirtualized?: false | undefined;
    } & SearchableListBoxProps)
  | ({
      /**
       * Whether the SearchableListBox should be virtualized.
       * If `true` then `maxHeight` is required.
       */
      isVirtualized: true;
      maxHeight: SearchableListBoxProps['maxHeight'];
    } & SearchableListBoxProps);

export const SearchableListBox = React.forwardRef<
  HTMLDivElement,
  SearchableListBoxWrapperProps
>(({isVirtualized, maxHeight, ...rest}, ref) => {
  const {isLoading} = useSearchableListContext();

  if (isLoading) {
    return (
      <View
        aria-label="Loading"
        display="flex"
        justifyContent="center"
        padding="$2 $0"
        ref={ref}
        role="status"
      >
        <Spinner />
      </View>
    );
  }

  if (isVirtualized && maxHeight) {
    return (
      <VirtualizedSearchableListBox ref={ref} {...rest} maxHeight={maxHeight} />
    );
  }

  return (
    <NonVirtualizedSearchableListBox
      ref={ref}
      {...rest}
      maxHeight={maxHeight}
    />
  );
});

SearchableListBox.displayName = 'SearchableListBox';
